import React, { ReactElement } from 'react';

import { useListSprints } from '@data/hooks/sprints';
import type { ProductEntity, SprintProductChapter } from '@data/utils/types';

import { personCost, rateIn, sumCosts, teamAt, type VersionTeam } from '../../products/productDetail/budget';
import { readDiscount } from '../../products/productDetail/discounts';
import { retentionLine } from '../../products/productDetail/retention';
import { readFractionalHours, readRoster } from '../../products/productDetail/fractional';
import { fixedChargeIn } from '../../products/productDetail/pricing';
import { useProductStore } from '../../products/productDetail/useProductStore';
import { useProductVersions } from '../../products/productDetail/useProductVersions';
import { useVersionTeams } from '../../products/productDetail/useVersionTeams';
import SprintTotals from './SprintTotals';

type Props = {
  product: ProductEntity;
  sprintId: string;
  /** The sprint's first day, which picks the terms it is priced under, as on Spend. */
  sprintStart: string;
  /** Everyone's hours, never the filter's: the cost is the sprint's own, like its hours. */
  groups: SprintProductChapter[];
  totalMinutes: number;
  /** Opening a product with no version for this year writes one, so a page that edits no money reads only. */
  readOnly?: boolean;
  costHref?: string;
};

/** Each member's hours at their rate on the product's team, the same rate Spend prices them at. */
export const sprintCost = (
  groups: SprintProductChapter[],
  rateFor: (membershipId: string) => number | null,
): { totalCost: number; unratedHours: number } => {
  let totalCost = 0;
  let unratedHours = 0;
  groups.forEach(group =>
    group.members.forEach(({ member, totalMinutes }) => {
      const rate = rateFor(member.id);
      if (rate === null) unratedHours += totalMinutes / 60;
      else totalCost += (totalMinutes / 60) * rate;
    }),
  );
  return { totalCost, unratedHours };
};

/** The bought-in rows Costs bills for the sprint, from the same roster and hours its grid reads. */
export const boughtInCost = (productId: string, sprintId: string, sprintStart: string): number => {
  const hours = readFractionalHours('spend', productId, sprintId);
  return sumCosts(
    readRoster(productId, sprintStart.slice(0, 4)).map(row => personCost(row.rate, hours[row.id] ?? null)),
  );
};

/** The sprint's price as Costs bills it, net of its discount and retention; `priced` is false when nothing had a rate. */
export const usePricedSprint = (
  product: ProductEntity | undefined,
  sprintId: string,
  sprintStart: string,
  groups: SprintProductChapter[],
  revision: number,
): { cost: number; unratedHours: number; priced: boolean; team: VersionTeam | null; sprintsReady: boolean } => {
  const productId = product?.id ?? '';
  const versions = useProductVersions(productId, String(revision));
  const teams = useVersionTeams(product, versions.versions, `${revision}:${versions.revision}`);
  const team = teamAt(teams, sprintStart);
  // The same list, and so the same cache entry, as the money tabs' year picker.
  const { data: sprints, isPending: sprintsPending } = useListSprints({ limit: 400 });
  const fixed = fixedChargeIn(team, sprintId, sprints?.items ?? []);
  const worked = sprintCost(groups, id => rateIn(team, id));
  // A fixed price is the sprint's whole charge for the team's hours, so none of them is left unpriced.
  const { totalCost, unratedHours } = fixed === null ? worked : { totalCost: fixed, unratedHours: 0 };
  const gross = totalCost + boughtInCost(productId, sprintId, sprintStart);
  // Less what Costs took off the sprint's bill, so both say the same figure.
  const net = gross - (readDiscount(productId, sprintId)?.amount ?? 0);
  const cost = net - retentionLine(productId, team, sprintId, net);
  return { cost, unratedHours, priced: gross !== 0 || unratedHours === 0, team, sprintsReady: !sprintsPending };
};

const Priced = ({
  product,
  sprintId,
  sprintStart,
  groups,
  totalMinutes,
  revision,
  costHref,
}: Omit<Props, 'readOnly'> & { revision: number }): ReactElement => {
  const { cost, unratedHours, priced } = usePricedSprint(product, sprintId, sprintStart, groups, revision);
  return (
    <SprintTotals
      totalMinutes={totalMinutes}
      totalCost={cost}
      unratedHours={unratedHours}
      priced={priced}
      costHref={costHref}
    />
  );
};

/** Hours alone until the product's figures are in, so nothing is priced off an empty store. */
const PricedSprintTotals = ({ readOnly, ...props }: Props): ReactElement => {
  const store = useProductStore(props.product.id, readOnly);
  if (!store.isReady) return <SprintTotals totalMinutes={props.totalMinutes} />;
  return <Priced {...props} revision={store.revision} />;
};

export default PricedSprintTotals;
